import {useMemo, useState} from 'react';
import {useRouteMatch} from 'react-router-dom';
import {QueryFunctionContext, useQuery} from 'react-query';
import {SingleValue} from 'react-select';

import ViewDetails from 'components/ViewDetails';

import {ListResponse, SelectOption, User} from 'types';
import api from 'api';
import {getFullName} from 'helpers/user';
import roles, {rolesObject} from 'constants/roles';

import useSort from 'hooks/useSort';
import useDebounce from 'hooks/useDebounce';
import {phoneFormat} from 'helpers/phoneFormat';
import useAuth from 'hooks/useAuth';

const filterOptions: SelectOption[] = [
  {value: '', label: 'All employees'},
  ...roles,
];

async function getEmployees({queryKey}: QueryFunctionContext<any[]>) {
  const [, role, order, keyWord, pageSize, currentPage] = queryKey;
  const {data} = await api.get<ListResponse<User>>('/users/', {
    params: {
      role,
      order,
      search: keyWord ? keyWord : undefined,
      limit: pageSize,
      offset: +currentPage * Number(pageSize),
    },
  });

  return data;
}

function useEmployees() {
  const {currentUser, rolePermission} = useAuth();
  const {url} = useRouteMatch();
  const [keyWord, setKeyWord] = useState(
    sessionStorage.getItem(`search_employe`) || ''
  );
  const [filterValue, setFilterValue] = useState<SelectOption>(
    filterOptions[0]
  );
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const {sortBy, sortOrder, order, onSort} = useSort('name');

  const searchDebounced = useDebounce(keyWord, 1000);

  const {data: employees, isLoading} = useQuery(
    [
      'employees',
      filterValue.value,
      order,
      searchDebounced,
      pageSize,
      currentPage - 1,
    ],
    getEmployees
  );

  const data = useMemo(
    function () {
      if (!employees) return [];

      return employees.results
        .filter(
          (employee) =>
            employee.role !== 'customer' && currentUser?.id !== employee.id
        )
        .map((employee) => {
          return {
            ...employee,
            role: rolesObject[employee.role as keyof typeof rolesObject],
            name: getFullName(employee),
            phone: phoneFormat(employee?.phone),
            view: <ViewDetails to={`${url}/${employee.id}`} />,
          };
        });
    },
    [employees, currentUser, url]
  );

  function onChangeFilter(newValue: SingleValue<SelectOption>) {
    setCurrentPage(1);
    newValue && setFilterValue(newValue);
  }

  const canWrite = rolePermission['employees']?.permissions?.write;

  return {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    data,
    isLoading,
    filterOptions,
    filterValue,
    sortBy,
    sortOrder,
    keyWord,
    setKeyWord,
    onChangeFilter,
    onSort,
    count: employees?.count || 0,
    canWrite: !!canWrite,
  };
}

export default useEmployees;
